import { AbyssTheme as themeConfiguration } from '@src/app/browser';
import { useApi } from '@src/app/context/Api';
import { SourcesTooltip } from '@src/entities/Source/ui/components/tooltips/Sources';
import { User } from '@src/entities/User/ui/components/User';
import { Button } from '@src/shared/ui/components/Button';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { useToast } from '@uhg-abyss/web/hooks/useToast';
import { dayjs } from '@uhg-abyss/web/tools/dayjs';
import { Card } from '@uhg-abyss/web/ui/Card';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Link } from '@uhg-abyss/web/ui/Link';
import { Text } from '@uhg-abyss/web/ui/Text';
import { Tooltip as AbyssTooltip } from '@uhg-abyss/web/ui/Tooltip';
import { isEmpty, isNil, isNull } from 'lodash';
import React, { useEffect, useState } from 'react';

import { Styles } from './includes/styles';

/**
 * Details
 *
 * Data containing sources and record ids for trusted and untrusted values.
 *
 * @param actionPath
 * @param eid
 * @param handleSearch
 * @returns {Element}
 * @constructor
 */
export function Details({ actionPath, eid, handleSearch }) {
  const [sourcesRemediated, setSourcesRemediated] = useState([]);
  const [link, setLink] = useState(null);

  const { toast } = useToast();

  const { clearApiCache, useApiMutation } = useApi('RiskRecord');
  const [RiskRecordActionPathDisassociation] = useApiMutation('RiskRecordActionPathDisassociation');

  /**
   * Store the sources that have been remediated.
   */
  useEffect(() => {
    const sources = [];

    actionPath?.recordActions?.forEach((recordAction) => {
      if (!isEmpty(recordAction?.recordReferences)) {
        recordAction?.recordReferences.forEach((recordReference) => {
          if (!sources.includes(recordReference?.source)) {
            sources.push(recordReference?.source);
          }
        });
      }
    });

    if (sources !== sourcesRemediated) {
      setSourcesRemediated(sources);
    }
  }, [actionPath?.recordActions]);

  /**
   * Set the action path link based on the status of the action path.
   */
  useEffect(() => {
    if (String(actionPath?.status).toLowerCase() === 'active') {
      setLink(`/action-paths/${actionPath?.actionPathId}`);
    } else if (String(actionPath?.status).toLowerCase() !== 'inactive') {
      setLink(`/action-paths/${actionPath?.actionPathId}/edit/${String(actionPath?.status).toLowerCase()}`);
    }
  }, [actionPath?.status, actionPath?.actionPathId]);

  /**
   * handleDetach
   *
   * Sends an api request to detach action paths from the EID.
   *
   * @returns {Promise<void>}
   */
  const handleDetach = async () => {
    const toastId = `actionPath-disassociation-${actionPath?.actionPathId}`;
    toast.show({
      ariaLoadingLabel: 'Detaching Action Paths from EID',
      autoClose: false,
      id: `${toastId}-info`,
      isLoading: true,
      message: 'Detaching Action Paths from EID...',
      title: 'Detaching Action Paths from EID',
      variant: 'info',
    });

    const payload = {
      actionPaths: [actionPath?.actionPathId],
      eids: [eid],
    };

    await RiskRecordActionPathDisassociation(payload, {
      onError: () => {
        toast.hide(`${toastId}-info`);
        toast.show({
          id: `${toastId}-error`,
          message: 'Unable to detach Action Paths.',
          title: 'Detaching Action Paths Failed',
          variant: 'error',
        });
      },
      onSuccess: async () => {
        await clearApiCache([{ apiKey: 'RiskRecord', queryKeys: ['GetRiskRecord'] }]);
        handleSearch();
        toast.hide(`${toastId}-info`);
        toast.show({
          id: `${toastId}-success`,
          message: 'Action Paths have been successfully detached.',
          title: 'Detached Action Paths from EID',
          variant: 'success',
        });
      },
    });
  };

  return (
    <ErrorHandler location="src/widgets/RiskRecords/ActionPaths/components/Details/Details.jsx">
      <Styles>
        <Card size="small">
          <Grid css={{ padding: 0 }}>
            <Grid.Col
              css={{ margin: '0px auto', paddingLeft: 0, paddingRight: 0 }}
              span={{ lg: '100%', md: '100%', sm: '100%', xl: '100%', xs: '100%' }}
            >
              <Grid>
                <Grid.Col css={{ borderRight: '1px solid var(--abyss-colors-gray4);' }}>
                  <Layout.Stack alignLayout="center">
                    <div>Status</div>
                    <div style={{ marginTop: themeConfiguration?.theme?.space?.xs }}>
                      <Text fontWeight="bold">{actionPath?.status}</Text>
                    </div>
                  </Layout.Stack>
                </Grid.Col>
                <Grid.Col css={{ borderRight: '1px solid var(--abyss-colors-gray4);' }}>
                  <Layout.Stack alignLayout="center">
                    <div>Last Updated</div>
                    <div style={{ marginTop: themeConfiguration?.theme?.space?.xs }}>
                      <Text fontWeight="bold">
                        {dayjs(actionPath?.lastModifiedDate).format('MM/DD/YYYY, HH:mm:ss')}
                      </Text>
                    </div>
                  </Layout.Stack>
                </Grid.Col>
                <Grid.Col css={{ borderRight: '1px solid var(--abyss-colors-gray4);' }}>
                  <Layout.Stack alignLayout="center">
                    <div>Source(s) Remediated</div>
                    <div style={{ marginTop: themeConfiguration?.theme?.space?.xs }}>
                      {sourcesRemediated.length > 1 ? (
                        <AbyssTooltip content={<SourcesTooltip sources={sourcesRemediated} />} placement="auto">
                          <Text fontWeight="bold">
                            <span className="truncate">{sourcesRemediated.join(', ')}</span>
                          </Text>
                        </AbyssTooltip>
                      ) : (
                        <React.Fragment>
                          {isNil(sourcesRemediated[0]) ? (
                            <Text color="var(--abyss-colors-gray4)">&mdash;</Text>
                          ) : (
                            <Text fontWeight="bold">{sourcesRemediated[0]}</Text>
                          )}
                        </React.Fragment>
                      )}
                    </div>
                  </Layout.Stack>
                </Grid.Col>
                <Grid.Col css={{ borderRight: '1px solid var(--abyss-colors-gray4);' }}>
                  <Layout.Stack alignLayout="center">
                    <div>Link</div>
                    <div style={{ marginTop: themeConfiguration?.theme?.space?.xs }}>
                      {!isNull(link) ? (
                        <Link href={link}>{actionPath?.name}</Link>
                      ) : (
                        <React.Fragment>{actionPath?.name}</React.Fragment>
                      )}
                    </div>
                  </Layout.Stack>
                </Grid.Col>
                {actionPath?.manualAssociation && (
                  <Grid.Col css={{ borderRight: '1px solid var(--abyss-colors-gray4);' }}>
                    <Layout.Stack alignLayout="center">
                      <div>Detach</div>
                      <div style={{ marginTop: themeConfiguration?.theme?.space?.xs }}>
                        <User.Capability attributes={['detach']} resource="ActionPaths">
                          <Button
                            before={<IconSymbol color="inherit" icon="remove_circle_outline" variant="outlined" />}
                            css={{
                              '& :hover .abyss-icon': {
                                color: 'white',
                              },
                            }}
                            data-testid="detach-action-path-button"
                            onClick={async () => {
                              await handleDetach();
                            }}
                            size="$sm"
                            variant="destructive"
                          >
                            Detach Action Path
                          </Button>
                        </User.Capability>
                      </div>
                    </Layout.Stack>
                  </Grid.Col>
                )}
              </Grid>
            </Grid.Col>
          </Grid>
        </Card>
      </Styles>
    </ErrorHandler>
  );
}
